import { Building2 } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { EmptyState } from '@/components/common'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { can, canUseDashboard, type Permission } from '@/lib/permissions'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  if (!user || !canUseDashboard(user)) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  return <Fragment key={`${user.id}:${user.role}:${user.viewer_station_id}`}>{children}</Fragment>
}

export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (user?.role !== 'superadmin') return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Operator screens. A superadmin first picks which operator to look at (the
 * backend needs `cpo_id` on every /cpo/* call); others need the permission.
 */
export function OperatorRoute({ children, perm }: { children: ReactNode; perm?: Permission }) {
  const { user, scope } = useAuth()
  const { t } = useI18n()
  if (!user) return null
  if (user.role === 'superadmin' && !scope) {
    return (
      <EmptyState
        icon={<Building2 />}
        title={t('Choose an operator to view')}
        body={t("Operator screens show one operator's network. Pick one with the switcher at the top, or go to the platform overview.")}
      />
    )
  }
  if (perm && !can(user, perm)) {
    return <EmptyState title={t("You don't have access to this page")} body={t('Ask your operator admin if you need it.')} />
  }
  return <Fragment key={`${user.id}:${user.role}:${user.viewer_station_id}:${scope}`}>{children}</Fragment>
}
